/**
 * Ingredients filter — include/exclude + per-ingredient dosage range.
 *
 * Data source: the ingredients-index* sections via the Section Rendering API
 * (collection, search, or catalogue-wide light index).
 * UI markup: snippets/ingredients-filter.liquid.
 *
 * Filter model:
 *  - Included ingredients combine with AND; each may carry a min/max dose.
 *  - Excluded ingredients hide products containing them at any dose.
 *  - A dosage range tests the ingredient's own listed amount, e.g.
 *    "Calcium 400 mg". Listings whose amount did not parse fail an active
 *    range test (conservative) but still count for include/exclude presence.
 */
(function () {
  'use strict';
  if (window.__ingredientsFilterLoaded) return;
  window.__ingredientsFilterLoaded = true;

  var CHUNK = 48;
  var MAX_INDEX_PAGES = 20;
  var MAX_INDEX_PAGES_ALL = 50; // light index paginates by 250 → 12,500 products
  var OPTION_LIMIT = 200;

  var UNITS = {
    'mcg': { family: 'mass', factor: 0.001 },
    'mg': { family: 'mass', factor: 1 },
    'g': { family: 'mass', factor: 1000 },
    'kg': { family: 'mass', factor: 1000000 },
    'iu': { family: 'iu', factor: 1 },
    'cfu': { family: 'cfu', factor: 1 },
    'billion cfu': { family: 'cfu', factor: 1000000000 },
    '%': { family: 'percent', factor: 1 }
  };
  var VALUE_RE = /^(.*?)(?:,\s*equaling)?\s*(?:<|&lt;)?\s*([\d][\d.,]*)\s*(billion\s+cfu|mcg|mg|kg|g|iu|cfu|%)\.?\s*$/i;
  var DOSE_RE = /^(?:<|&lt;)?\s*([\d][\d.,]*)\s*(billion\s+cfu|mcg|mg|kg|g|iu|cfu|%)\.?\s*$/i;
  var DEFAULT_UNIT_OPTIONS = ['mg', 'mcg', 'g', 'iu', 'cfu', 'billion cfu', '%'];

  // state.inc: [{key, label, min, max, unit}] — state.exc: [{key, label}]
  // state.all: scope — false = current collection, true = entire catalogue
  var state = { inc: [], exc: [], all: false };
  var uiMode = 'inc'; // which tab is active: 'inc' | 'exc'
  var catalog = null;        // {ingredients: {key: meta}, products: [...]}
  var catalogUrl = null;     // index URL the catalog was built from
  var catalogCache = {};     // index URL -> built catalog, so scope toggles don't refetch
  var loading = false;
  var visibleCount = CHUNK;
  var els = {};

  /* ---------------- parsing ---------------- */

  function parseValue(raw) {
    var text = String(raw).trim();
    var m = text.match(VALUE_RE);
    if (!m || !m[1]) return { name: text, unit: null, base: null };
    var unit = m[3].toLowerCase().replace(/\s+/g, ' ');
    var amount = parseFloat(m[2].replace(/,/g, ''));
    if (!UNITS[unit] || isNaN(amount)) return { name: text, unit: null, base: null };
    return { name: m[1].trim(), unit: unit, base: amount * UNITS[unit].factor };
  }

  function cleanName(name) {
    var n = String(name)
      .replace(/\s*\[[^\]]*\]/g, '')
      .replace(/\s*\([^)]*\)/g, '');
    n = n.split(/\s+-|,\s|\s*\(|\s*\[/)[0] || n;
    return n.replace(/\s+/g, ' ').trim();
  }

  function baseKey(name) {
    return cleanName(name).toLowerCase();
  }

  // Variant dosage from blue_sky_app.Dosage, e.g. "500 mg".
  function parseDose(raw) {
    var m = String(raw).trim().match(DOSE_RE);
    if (!m) return null;
    var unit = m[2].toLowerCase().replace(/\s+/g, ' ');
    var amount = parseFloat(m[1].replace(/,/g, ''));
    if (!UNITS[unit] || isNaN(amount)) return null;
    return { unit: unit, base: amount * UNITS[unit].factor };
  }

  /* ---------------- catalog ---------------- */

  function buildCatalog(rawProducts) {
    var ingredients = {};
    var products = [];
    rawProducts.forEach(function (p) {
      var doses = (p.dos || []).map(parseDose).filter(Boolean);
      var ing = {};
      (p.ing || []).forEach(function (raw) {
        var v = parseValue(raw);
        var key = baseKey(v.name);
        if (!key) return;
        (ing[key] = ing[key] || []).push(v);
        var meta = ingredients[key];
        if (!meta) {
          meta = ingredients[key] = { label: cleanName(v.name), productCount: 0, unitCounts: {}, seen: {} };
        }
        // Unit options reflect the units this ingredient is actually listed
        // in across the catalog, because the range tests those amounts.
        if (v.unit) {
          meta.unitCounts[v.unit] = (meta.unitCounts[v.unit] || 0) + 1;
        }
        if (!meta.seen[p.handle]) {
          meta.seen[p.handle] = true;
          meta.productCount += 1;
        }
      });
      // doses (variant serving sizes) are kept for reference; the dosage
      // range test runs on the per-ingredient amounts in ing.
      products.push({ handle: p.handle, card: p.card, ing: ing, doses: doses });
    });

    Object.keys(ingredients).forEach(function (key) {
      var meta = ingredients[key];
      var best = null;
      Object.keys(meta.unitCounts).forEach(function (u) {
        if (!best || meta.unitCounts[u] > meta.unitCounts[best]) best = u;
      });
      meta.unit = best; // dominant display unit, null if doses never parsed
      delete meta.seen;
    });

    return { ingredients: ingredients, products: products };
  }

  /* ---------------- index fetch ---------------- */

  function indexUrlBase() {
    if (state.all) {
      // Catalogue-wide scope: one canonical URL. Native facet params are
      // deliberately dropped — "entire catalogue" means unfiltered — and the
      // light index keeps ~9k products fetchable.
      var allUrl = els.root.getAttribute('data-all-url') || '/collections/all';
      return allUrl + '?sections=ingredients-index-light';
    }
    var params = new URLSearchParams(location.search);
    params.delete('ing_inc');
    params.delete('ing_exc');
    params.delete('page');
    params.delete('sections');
    // Superseded by this UI — never forward the legacy facet.
    params.delete('filter.p.m.custom.ingredients_filter');
    params.set('sections', els.root.getAttribute('data-index-section') || 'ingredients-index');
    var url = els.root.getAttribute('data-collection-url') || location.pathname;
    return url + '?' + params.toString();
  }

  function fetchIndexPage(base, page) {
    return fetch(base + '&page=' + page, { credentials: 'same-origin' })
      .then(function (r) {
        if (!r.ok) throw new Error('HTTP ' + r.status);
        return r.json();
      })
      .then(function (json) {
        var html = json['ingredients-index-light'] || json['ingredients-index-search'] || json['ingredients-index'];
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var script = doc.querySelector('[data-ingredients-index]');
        if (!script) throw new Error('index payload missing');
        return JSON.parse(script.textContent);
      });
  }

  // Fetch pages 2..last with limited concurrency and one retry each — the
  // catalogue-wide index is ~38 pages and a single flaky response must not
  // fail the whole load.
  function fetchIndexPages(base, lastPage) {
    var pages = [];
    for (var p = 2; p <= lastPage; p++) pages.push(p);
    var results = new Array(pages.length);
    var done = 0;
    var i = 0;
    function report() {
      done += 1;
      if (lastPage > 10) setStatus('Loading ingredients… (' + (done + 1) + '/' + lastPage + ')');
    }
    function next() {
      if (i >= pages.length) return Promise.resolve();
      var idx = i++;
      return fetchIndexPage(base, pages[idx])
        .catch(function () { return fetchIndexPage(base, pages[idx]); })
        .then(function (r) {
          results[idx] = r;
          report();
          return next();
        });
    }
    var workers = [];
    for (var w = 0; w < Math.min(6, pages.length); w++) workers.push(next());
    return Promise.all(workers).then(function () { return results; });
  }

  var catalogPromise = null;
  var catalogPromiseUrl = null;

  function loadCatalog() {
    var base = indexUrlBase();
    if (catalog && catalogUrl === base) return Promise.resolve(catalog);
    if (catalogCache[base]) {
      catalog = catalogCache[base];
      catalogUrl = base;
      return Promise.resolve(catalog);
    }
    if (catalogPromise && catalogPromiseUrl === base) return catalogPromise;
    var maxPages = state.all ? MAX_INDEX_PAGES_ALL : MAX_INDEX_PAGES;
    loading = true;
    setStatus('Loading ingredients…');
    catalogPromiseUrl = base;
    catalogPromise = fetchIndexPage(base, 1)
      .then(function (first) {
        var pages = Math.min(first.pages || 1, maxPages);
        return fetchIndexPages(base, pages).then(function (more) {
          var all = first.products.slice();
          more.forEach(function (chunk) { all = all.concat(chunk.products); });
          catalog = buildCatalog(all);
          catalog.truncated = (first.pages || 1) > maxPages;
          catalog.scanned = all.length;
          catalogCache[base] = catalog;
          catalogUrl = base;
          loading = false;
          return catalog;
        });
      })
      .catch(function (err) {
        loading = false;
        catalog = null;
        catalogPromise = null;
        catalogPromiseUrl = null;
        setStatus('Couldn’t load ingredients. <a data-ing-retry>Retry</a>');
        throw err;
      });
    return catalogPromise;
  }

  /* ---------------- filtering ---------------- */

  function productMatches(prod) {
    var i;
    for (i = 0; i < state.exc.length; i++) {
      if (prod.ing[state.exc[i].key]) return false;
    }
    for (i = 0; i < state.inc.length; i++) {
      var rule = state.inc[i];
      if (!prod.ing[rule.key]) return false;
      if (rule.min != null || rule.max != null) {
        var meta = catalog.ingredients[rule.key];
        var unitName = rule.unit || (meta && meta.unit);
        var unit = unitName ? UNITS[unitName] : null;
        if (!unit) return false;
        var lo = rule.min != null ? rule.min * unit.factor : -Infinity;
        var hi = rule.max != null ? rule.max * unit.factor : Infinity;
        // The range tests the amount of THIS ingredient as listed on the
        // product ("Calcium 400 mg"), not the variant serving size: the
        // product passes if any listing of it falls inside the range.
        var ok = prod.ing[rule.key].some(function (v) {
          return v.base != null && v.unit && UNITS[v.unit]
            && UNITS[v.unit].family === unit.family
            && v.base >= lo && v.base <= hi;
        });
        if (!ok) return false;
      }
    }
    return true;
  }

  function activeRuleCount() {
    return state.inc.length + state.exc.length;
  }

  /* ---------------- URL state ---------------- */

  function readStateFromUrl() {
    var params = new URLSearchParams(location.search);
    state.inc = [];
    state.exc = [];
    state.all = params.get('ing_all') === '1';
    (params.get('ing_inc') || '').split('|').forEach(function (part) {
      if (!part) return;
      var bits = part.split('~');
      var label = bits[0].trim();
      if (!label) return;
      state.inc.push({
        key: baseKey(label),
        label: label,
        min: bits[1] !== undefined && bits[1] !== '' ? parseFloat(bits[1]) : null,
        max: bits[2] !== undefined && bits[2] !== '' ? parseFloat(bits[2]) : null,
        unit: bits[3] && UNITS[bits[3]] ? bits[3] : null
      });
    });
    (params.get('ing_exc') || '').split('|').forEach(function (part) {
      var label = part.trim();
      if (label) state.exc.push({ key: baseKey(label), label: label });
    });
  }

  function writeStateToUrl() {
    var params = new URLSearchParams(location.search);
    var inc = state.inc.map(function (r) {
      return r.label + '~' + (r.min != null ? r.min : '') + '~' + (r.max != null ? r.max : '') + '~' + (r.unit || '');
    }).join('|');
    var exc = state.exc.map(function (r) { return r.label; }).join('|');
    if (inc) params.set('ing_inc', inc); else params.delete('ing_inc');
    if (exc) params.set('ing_exc', exc); else params.delete('ing_exc');
    if (state.all) params.set('ing_all', '1'); else params.delete('ing_all');
    var qs = params.toString();
    history.replaceState(history.state, '', location.pathname + (qs ? '?' + qs : '') + location.hash);
  }

  /* ---------------- results grid ---------------- */

  function nativeGrid() {
    return document.querySelector('[data-productgrid-items]');
  }

  function nativePagination() {
    return document.querySelector('.pagination--container');
  }

  function resultsContainer() {
    var grid = nativeGrid();
    if (!grid) return null;
    var res = document.querySelector('[data-ing-results]');
    if (!res || !res.isConnected) {
      res = document.createElement('div');
      res.setAttribute('data-ing-results', '');
      res.className = grid.className;
      grid.parentNode.insertBefore(res, grid.nextSibling);

      var wrap = document.createElement('div');
      wrap.setAttribute('data-ing-loadmore-wrap', '');
      wrap.style.textAlign = 'center';
      wrap.style.margin = '20px 0';
      wrap.innerHTML = '<button type="button" data-ing-loadmore hidden style="cursor:pointer;padding:10px 24px;">Load more</button>';
      res.parentNode.insertBefore(wrap, res.nextSibling);
    }
    return res;
  }

  function renderResults() {
    var res = resultsContainer();
    if (!res) return;
    var matched = catalog.products.filter(productMatches);
    var slice = matched.slice(0, visibleCount);

    res.innerHTML = slice.length
      ? slice.map(function (p) { return p.card; }).join('')
      : '<div class="productgrid--no-results" style="grid-column:1/-1;">' +
        '<h2 class="productgrid--no-results-title">No products match these ingredients</h2>' +
        (state.all ? '' : '<p>Try ticking &ldquo;Expand search to entire catalogue&rdquo; to search all products.</p>') +
        '</div>';

    var more = document.querySelector('[data-ing-loadmore]');
    if (more) more.hidden = matched.length <= visibleCount;

    var scopeNote = state.all ? ' across the entire catalogue' : '';
    var truncatedNote = catalog.truncated
      ? ' (searched first ' + catalog.scanned + ' products)'
      : '';
    setStatus(matched.length + ' product' + (matched.length === 1 ? '' : 's') + scopeNote + truncatedNote +
      ' · <a data-ing-clear-link>Clear all</a>');
  }

  function showNative(show) {
    var grid = nativeGrid();
    var pag = nativePagination();
    var res = document.querySelector('[data-ing-results]');
    var wrap = document.querySelector('[data-ing-loadmore-wrap]');
    if (grid) grid.style.display = show ? '' : 'none';
    if (pag) pag.style.display = show ? '' : 'none';
    if (res) res.style.display = show ? 'none' : '';
    if (wrap) wrap.style.display = show ? 'none' : '';
  }

  /* ---------------- sidebar UI ---------------- */

  function setStatus(html) {
    if (!els.status) return;
    els.status.hidden = !html;
    els.status.innerHTML = html || '';
  }

  function escapeHtml(s) {
    return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;')
      .replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  function renderSelected() {
    if (!els.selected) return;
    var html = '';

    if (uiMode === 'inc') {
      state.inc.forEach(function (r, i) {
        var meta = catalog && catalog.ingredients[r.key];
        var units = meta ? Object.keys(meta.unitCounts).sort(function (a, b) {
          return meta.unitCounts[b] - meta.unitCounts[a];
        }) : [];
        // Always offer the dosage controls, even when no variant dosage was
        // observed yet for this ingredient's products.
        if (!units.length) units = DEFAULT_UNIT_OPTIONS.slice();
        var selectedUnit = r.unit || (meta && meta.unit) || units[0];

        html += '<div class="ingredients-filter__row" data-ing-row="inc:' + i + '">' +
          '<div class="ingredients-filter__chip">' +
          '<span class="ingredients-filter__row-name">' + escapeHtml(r.label) + '</span>' +
          '<button type="button" class="ingredients-filter__row-remove" data-ing-remove="inc:' + i + '" aria-label="Remove ' + escapeHtml(r.label) + '">✕</button>' +
          '</div>';
        {
          html += '<div class="ingredients-filter__dose-grid">' +
            '<input type="number" min="0" step="any" inputmode="decimal" placeholder="Min dosage" aria-label="Min dosage" value="' + (r.min != null ? r.min : '') + '" data-ing-dose="min:' + i + '">' +
            '<input type="number" min="0" step="any" inputmode="decimal" placeholder="Max dosage" aria-label="Max dosage" value="' + (r.max != null ? r.max : '') + '" data-ing-dose="max:' + i + '">' +
            '</div>' +
            '<label class="ingredients-filter__unit">' +
            '<span>Select units</span>' +
            '<select data-ing-unit="' + i + '">' +
            units.map(function (u) {
              return '<option value="' + escapeHtml(u) + '"' + (u === selectedUnit ? ' selected' : '') + '>' + escapeHtml(u) + '</option>';
            }).join('') +
            '</select>' +
            '</label>';
        }
        html += '</div>';
      });
    } else {
      state.exc.forEach(function (r, i) {
        html += '<div class="ingredients-filter__row ingredients-filter__row--exclude" data-ing-row="exc:' + i + '">' +
          '<div class="ingredients-filter__chip">' +
          '<span class="ingredients-filter__row-name">' + escapeHtml(r.label) + '</span>' +
          '<button type="button" class="ingredients-filter__row-remove" data-ing-remove="exc:' + i + '" aria-label="Remove ' + escapeHtml(r.label) + '">✕</button>' +
          '</div></div>';
      });
    }

    els.selected.innerHTML = html;
    if (els.clear) els.clear.hidden = activeRuleCount() === 0;
    if (els.badge) {
      els.badge.hidden = activeRuleCount() === 0;
      els.badge.innerHTML = '<span>' + activeRuleCount() + '</span>';
    }
  }

  function renderOptions() {
    if (!els.options || !catalog) return;
    var q = (els.search && els.search.value || '').trim().toLowerCase();
    var current = {};
    (uiMode === 'inc' ? state.inc : state.exc).forEach(function (r) { current[r.key] = true; });

    // Most common ingredients first — matches the "Common ingredients" heading.
    var keys = Object.keys(catalog.ingredients).filter(function (k) {
      return !q || k.indexOf(q) !== -1;
    }).sort(function (a, b) {
      var d = catalog.ingredients[b].productCount - catalog.ingredients[a].productCount;
      return d !== 0 ? d : catalog.ingredients[a].label.localeCompare(catalog.ingredients[b].label);
    });

    var html = keys.slice(0, OPTION_LIMIT).map(function (k) {
      var meta = catalog.ingredients[k];
      return '<li class="ingredients-filter__option"><label>' +
        '<input type="checkbox" data-ing-check="' + escapeHtml(k) + '"' + (current[k] ? ' checked' : '') + '>' +
        '<span class="ingredients-filter__option-name">' + escapeHtml(meta.label) +
        ' <span class="ingredients-filter__option-count">(' + meta.productCount + ')</span></span>' +
        '</label></li>';
    }).join('');

    if (!keys.length) {
      html = '<li class="ingredients-filter__option"><span class="ingredients-filter__option-name">No ingredients found</span></li>';
    }
    els.options.innerHTML = html;
  }

  /* ---------------- orchestration ---------------- */

  function apply() {
    writeStateToUrl();
    renderSelected();

    if (activeRuleCount() === 0) {
      showNative(true);
      setStatus('');
      return;
    }

    loadCatalog().then(function () {
      visibleCount = CHUNK;
      renderSelected(); // dose units may only be known post-load
      renderOptions();
      showNative(false);
      renderResults();
    }).catch(function () { /* status already set */ });
  }

  function openGroup() {
    if (!els.trigger || !els.body) return;
    els.trigger.setAttribute('aria-expanded', 'true');
    els.trigger.classList.add('productgrid--sidebar-button-active');
    els.body.setAttribute('data-accordion-state', 'open');
    els.body.classList.add('filter-group-active');
  }

  function scan() {
    els.root = document.querySelector('[data-ingredients-filter]');
    if (!els.root) return false;
    els.trigger = els.root.querySelector('[data-filter-group-trigger]');
    els.body = els.root.querySelector('[data-accordion-content]');
    els.status = els.root.querySelector('[data-ing-status]');
    els.search = els.root.querySelector('[data-ing-search]');
    els.options = els.root.querySelector('[data-ing-options]');
    els.selected = els.root.querySelector('[data-ing-selected]');
    els.clear = els.root.querySelector('[data-ing-clear]');
    els.badge = els.root.querySelector('[data-ing-badge]');
    els.expand = els.root.querySelector('[data-ing-expand]');
    return true;
  }

  function syncExpand() {
    if (els.expand) els.expand.checked = state.all;
    // Catalogue-wide scope makes the collection heading misleading, so hide it.
    // A body class, not an inline style, survives the theme AJAX re-render.
    if (document.body) document.body.classList.toggle('ingredients-scope-all', state.all);
  }

  function init() {
    if (!scan()) return;
    if (!els.root.getAttribute('data-collection-url')) return;
    readStateFromUrl();
    syncExpand();
    renderSelected();
    // The group starts expanded with its ingredient list populated.
    openGroup();
    loadCatalog().then(function () {
      renderOptions();
      if (activeRuleCount() === 0) setStatus('');
    }).catch(function () {});
    if (activeRuleCount() > 0) {
      apply();
    }
  }

  /* ---------------- events (delegated) ---------------- */

  function findRule(ref) {
    var bits = ref.split(':');
    var list = bits[0] === 'inc' ? state.inc : state.exc;
    return { list: bits[0], arr: list, i: parseInt(bits[1], 10) };
  }

  document.addEventListener('click', function (e) {
    if (!e.target.closest) return;

    // Lazily load the option list the first time the group is opened.
    if (e.target.closest('[data-ingredients-filter] [data-filter-group-trigger]')) {
      loadCatalog().then(function () {
        renderOptions();
        if (activeRuleCount() === 0) setStatus('');
      }).catch(function () {});
      return;
    }

    var t = e.target.closest('[data-ing-tab],[data-ing-remove],[data-ing-clear],[data-ing-clear-link],[data-ing-retry],[data-ing-loadmore]');
    if (!t) return;

    if (t.hasAttribute('data-ing-tab')) {
      uiMode = t.getAttribute('data-ing-tab') === 'exc' ? 'exc' : 'inc';
      if (els.root) {
        els.root.querySelectorAll('[data-ing-tab]').forEach(function (tab) {
          tab.setAttribute('aria-selected', String(tab === t));
        });
      }
      renderSelected();
      renderOptions();
    } else if (t.hasAttribute('data-ing-remove')) {
      var ref = findRule(t.getAttribute('data-ing-remove'));
      ref.arr.splice(ref.i, 1);
      renderOptions();
      apply();
    } else if (t.hasAttribute('data-ing-clear') || t.hasAttribute('data-ing-clear-link')) {
      state.inc = [];
      state.exc = [];
      renderOptions();
      apply();
    } else if (t.hasAttribute('data-ing-retry')) {
      apply();
    } else if (t.hasAttribute('data-ing-loadmore')) {
      visibleCount += CHUNK;
      renderResults();
    }
  });

  document.addEventListener('change', function (e) {
    var t = e.target;
    if (!t.hasAttribute) return;

    if (t.hasAttribute('data-ing-unit')) {
      var rule = state.inc[parseInt(t.getAttribute('data-ing-unit'), 10)];
      if (!rule || !UNITS[t.value]) return;
      rule.unit = t.value;
      writeStateToUrl();
      if (activeRuleCount() > 0 && catalog) renderResults();
    } else if (t.hasAttribute('data-ing-expand')) {
      state.all = !!t.checked;
      syncExpand();
      writeStateToUrl();
      // Reload even with no active rules so the option list and counts
      // reflect the new scope; the grid only swaps when rules are active.
      loadCatalog().then(function () {
        renderSelected();
        renderOptions();
        if (activeRuleCount() > 0) {
          visibleCount = CHUNK;
          showNative(false);
          renderResults();
        } else {
          setStatus('');
        }
      }).catch(function () { /* status already set */ });
    } else if (t.hasAttribute('data-ing-check')) {
      var key = t.getAttribute('data-ing-check');
      var arr = uiMode === 'inc' ? state.inc : state.exc;
      var other = uiMode === 'inc' ? state.exc : state.inc;
      var idx = arr.findIndex(function (r) { return r.key === key; });

      if (t.checked && idx === -1) {
        // An ingredient can't be included and excluded at once.
        var oidx = other.findIndex(function (r) { return r.key === key; });
        if (oidx !== -1) other.splice(oidx, 1);
        var meta = catalog && catalog.ingredients[key];
        var label = meta ? meta.label : key;
        if (uiMode === 'inc') state.inc.push({ key: key, label: label, min: null, max: null, unit: null });
        else state.exc.push({ key: key, label: label });
      } else if (!t.checked && idx !== -1) {
        arr.splice(idx, 1);
      }
      renderOptions();
      apply();
    }
  });

  var doseTimer = null;
  document.addEventListener('input', function (e) {
    var t = e.target;
    if (t.hasAttribute && t.hasAttribute('data-ing-dose')) {
      var bits = t.getAttribute('data-ing-dose').split(':');
      var rule = state.inc[parseInt(bits[1], 10)];
      if (!rule) return;
      var v = t.value === '' ? null : parseFloat(t.value);
      if (bits[0] === 'min') rule.min = isNaN(v) ? null : v;
      else rule.max = isNaN(v) ? null : v;
      clearTimeout(doseTimer);
      doseTimer = setTimeout(function () {
        writeStateToUrl();
        if (activeRuleCount() > 0 && catalog) renderResults();
      }, 350);
    } else if (t === els.search) {
      renderOptions();
    }
  });

  // The theme re-renders the sidebar and grid after AJAX filtering without
  // dispatching any event, so watch for our root node being replaced.
  var rebindTimer = null;
  new MutationObserver(function () {
    if (els.root && els.root.isConnected) return;
    clearTimeout(rebindTimer);
    rebindTimer = setTimeout(function () {
      if (!scan()) return;
      // Native filter params may differ — invalidate and refetch.
      catalogUrl = null;
      catalogPromise = null;
      catalogPromiseUrl = null;
      readStateFromUrl();
      syncExpand();
      renderSelected();
      openGroup();
      loadCatalog().then(function () {
        renderOptions();
        if (activeRuleCount() === 0) setStatus('');
      }).catch(function () {});
      if (activeRuleCount() > 0) {
        apply();
      }
    }, 50);
  }).observe(document.documentElement, { childList: true, subtree: true });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
